/**
 * Resumable upload hook for image-tools-ui.
 * Mirrors the Pydfui hook but reads API_CONFIG for the base URL.
 */
import { useState, useEffect, useMemo } from 'react';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import { API_CONFIG } from '../config/api';

export function extractTusUploadId(tusUrl: string): string {
  const parts = tusUrl.replace(/\/$/, '').split('/');
  return parts[parts.length - 1];
}

export function useTusUpload(maxFileSize = API_CONFIG.MAX_FILE_SIZE) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uppy = useMemo(
    () =>
      new Uppy({
        restrictions: { maxFileSize, maxNumberOfFiles: 1 },
        autoProceed: false,
        debug: true, // verbose console logs for debugging resume flow
      })
        .use(Tus, {
          endpoint: `${API_CONFIG.BASE_URL}/tus/uploads/`,
          chunkSize: 5 * 1024 * 1024,
          retryDelays: [0, 1000, 3000, 5000, 10000],
          // Keep URL fingerprint in localStorage so re-selecting the same file
          // after a refresh resumes from the server-confirmed offset.
          removeFingerprintOnSuccess: true,
        }),
    [maxFileSize],
  );

  useEffect(() => {
    const onProgress = (_file: unknown, p: { bytesUploaded: number; bytesTotal: number | null }) => {
      if (p.bytesTotal && p.bytesTotal > 0) setProgress((p.bytesUploaded / p.bytesTotal) * 100);
    };
    const onFileAdded = (f: unknown) => console.log('[TUS] file-added', f);
    const onUploadStart = (data: unknown) => console.log('[TUS] upload-start', data);
    const onError = (err: unknown) => console.error('[TUS] error', err);
    const onUploadSuccess = (f: unknown, response: unknown) => console.log('[TUS] upload-success', { file: f, response });

    uppy.on('upload-progress', onProgress);
    uppy.on('file-added', onFileAdded);
    uppy.on('upload', onUploadStart);
    uppy.on('upload-success', onUploadSuccess);
    uppy.on('error', onError);
    return () => {
      uppy.off('upload-progress', onProgress);
      uppy.off('file-added', onFileAdded);
      uppy.off('upload', onUploadStart);
      uppy.off('upload-success', onUploadSuccess);
      uppy.off('error', onError);
    };
  }, [uppy]);

  const upload = (file: File): Promise<string> => {
    setUploading(true);
    setProgress(0);
    setError(null);

    return new Promise((resolve, reject) => {
      const onSuccess = (f: unknown, response: unknown) => {
        cleanup();
        setUploading(false);
        setProgress(100);
        const file = f as { uploadURL?: string } | undefined;
        const resp = response as { uploadURL?: string; body?: { uploadURL?: string } } | undefined;
        const url = resp?.uploadURL ?? file?.uploadURL ?? resp?.body?.uploadURL;
        if (!url) {
          console.error('[TUS] could not find uploadURL', { file, response });
          return reject(new Error('TUS upload finished without uploadURL'));
        }
        resolve(url);
      };
      const onFailure = (_f: unknown, err: unknown) => {
        cleanup();
        setUploading(false);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        reject(new Error(msg));
      };
      const cleanup = () => {
        uppy.off('upload-success', onSuccess);
        uppy.off('upload-error', onFailure);
      };
      uppy.on('upload-success', onSuccess);
      uppy.on('upload-error', onFailure);

      try {
        uppy.addFile({ name: file.name, type: file.type, data: file });
        uppy.upload();
      } catch (err) {
        cleanup();
        setUploading(false);
        reject(err);
      }
    });
  };

  return { upload, progress, uploading, error };
}
